import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import React, { useState, useEffect } from 'react';
import {
  X, ArrowRight, AlertTriangle, CheckCircle, Loader2, Mail, FileText,
  Bell, Calendar, ClipboardList, User, Building2, MessageSquare, Upload, Paperclip, Car
} from 'lucide-react';
import { supplierAPI, workflowOutputAPI, documentAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';
import i18n from '../../i18n/index.js';

/**
 * WorkflowTransitionModal - Modal for executing workflow state transitions
 *
 * Features:
 * - Status change selection with validation
 * - Required fields based on transition (rejection_reason, supplier, etc.)
 * - Notes input for audit trail
 * - Display of outputs that will be triggered
 * - Supplier selection when applicable
 */

// Workflow configurations per type — labels resolved via i18n getters so this
// module-level constant stays fresh across language changes (it is built once
// at import time, but each `.label` read re-resolves it — see
// docs/implementations/group/I18N_CONVENTIONS.md, piège #1).
const WORKFLOW_META = {
  claim: {
    statusColors: {
      // Legacy English statuses
      draft: 'gray',
      declared: 'blue',
      acknowledged: 'indigo',
      expert_done: 'purple',
      repair_authorized: 'cyan',
      indemnified: 'emerald',
      closed: 'green',
      rejected: 'red',
      // French workflow statuses (current)
      brouillon: 'gray',
      declaration_prete: 'orange',
      declaration_envoyee: 'blue',
      en_instruction: 'indigo',
      expert_pending: 'yellow',
      expertise_realisee: 'purple',
      rapport_recu: 'purple',
      prise_en_charge_demandee: 'cyan',
      en_reparation: 'cyan',
      attente_facture_garage: 'amber',
      vei: 'red',
      offre_vei_recue: 'orange',
      vei_conserve: 'red',
      attente_indemnisation: 'emerald',
      cloture: 'green',
    },
    transitions: {
      // Legacy
      draft: ['declared'],
      declared: ['acknowledged'],
      acknowledged: ['expert_pending'],
      expert_done: ['rapport_recu'],
      repair_authorized: ['en_reparation'],
      indemnified: ['cloture'],
      closed: [],
      rejected: [],
      // French workflow (current)
      brouillon: ['declaration_prete'],
      declaration_prete: ['declaration_envoyee'],
      declaration_envoyee: ['en_instruction'],
      en_instruction: ['expert_pending'],
      expert_pending: ['expertise_realisee'],
      expertise_realisee: ['rapport_recu', 'vei'],
      rapport_recu: ['prise_en_charge_demandee', 'vei'],
      prise_en_charge_demandee: ['en_reparation', 'vei'],
      en_reparation: ['attente_facture_garage'],
      attente_facture_garage: ['attente_indemnisation'],
      vei: ['attente_indemnisation'],
      attente_indemnisation: ['cloture'],
      cloture: [],
    },
    requiredFields: {
      rejected: ['rejection_reason'],
      en_instruction: ['insurer_reference', 'liability'], // AR from broker: ref + liability
      expert_pending: ['expert_supplier_id'], // validated custom: supplier OR manual name
      expertise_realisee: ['expert_conclusion'],
      rapport_recu: ['montant_expertise'],
      prise_en_charge_demandee: ['garage_supplier_id'],
      attente_indemnisation: ['invoice_amount'],
      indemnified: ['invoice_amount']
    },
    supplierCategories: {
      expert_pending: 'expert',
      prise_en_charge_demandee: 'garage'
    }
  },
  maintenance: {
    statusColors: {
      draft: 'gray',
      requested: 'blue',
      approved: 'green',
      in_progress: 'yellow',
      completed: 'emerald',
      rejected: 'red',
      cancelled: 'gray'
    },
    transitions: {
      draft: ['requested'],
      requested: ['approved', 'rejected'],
      approved: ['in_progress', 'cancelled'],
      in_progress: ['completed', 'cancelled'],
      completed: [],
      rejected: [],
      cancelled: []
    },
    requiredFields: {
      rejected: ['rejection_reason'],
      approved: ['supplier_id'],
      cancelled: ['cancellation_reason']
    },
    supplierCategories: {
      approved: 'garage'
    }
  },
  inspection: {
    statusColors: {
      scheduled: 'blue',
      in_progress: 'yellow',
      completed: 'green',
      failed: 'red',
      cancelled: 'gray'
    },
    transitions: {
      scheduled: ['in_progress', 'cancelled'],
      in_progress: ['completed', 'failed'],
      completed: [],
      failed: ['scheduled'],
      cancelled: ['scheduled']
    },
    requiredFields: {
      failed: ['failure_reason'],
      cancelled: ['cancellation_reason']
    },
    supplierCategories: {}
  }
};

function buildStatuses(type, statusColors) {
  return Object.fromEntries(
    Object.entries(statusColors).map(([code, color]) => [
      code,
      {
        color,
        get label() {
          return i18n.t(`common.workflow.statuses.${type}.${code}`);
        }
      }
    ])
  );
}

const WORKFLOW_CONFIGS = Object.fromEntries(
  Object.entries(WORKFLOW_META).map(([type, meta]) => [
    type,
    {
      get label() {
        return i18n.t(`common.workflow.types.${type}`);
      },
      statuses: buildStatuses(type, meta.statusColors),
      transitions: meta.transitions,
      requiredFields: meta.requiredFields,
      supplierCategories: meta.supplierCategories
    }
  ])
);

/**
 * Jour calendaire (YYYY-MM-DD) d'une valeur de date, dans le fuseau du poste.
 *
 * Un `.split('T')[0]` sur la valeur rendue par l'API se tromperait d'un jour :
 * les colonnes datetime sont stockées sans fuseau, si bien qu'un rendez-vous au
 * 11/09 revient « 2026-09-10T22:00:00.000Z ». Même helper que côté serveur
 * (calendarDay dans backend/routes/workflow_routes.mjs).
 */
function calendarDay(value) {
  if (!value) return null;
  const s = String(value);
  const bare = s.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (bare) return bare[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.split('T')[0];
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Output type icons
const OUTPUT_ICONS = {
  email: Mail,
  document: FileText,
  notification: Bell,
  task: ClipboardList,
  event: Calendar
};

function WorkflowTransitionModal({
  isOpen,
  onClose,
  onConfirm,
  workflowType = 'claim',
  currentStatus,
  entityId,
  entityReference,
  entityName,
  currentUser,
  contextData
}) {
  // Devise du dossier traite - un seul outil monetaire.
  const { money, symbolOf } = useOrgCurrency();
  const { t, fmt } = useFormat();
  const ctxMoney = (v) => money(v, contextData);
  const ctxSymbol = symbolOf(contextData);

  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [repairAmount, setRepairAmount] = useState('');
  const [indemnificationRecipient, setIndemnificationRecipient] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [expertRdvDate, setExpertRdvDate] = useState('');
  const [montantExpertise, setMontantExpertise] = useState('');
  const [expertConclusion, setExpertConclusion] = useState('');
  // Date RÉELLE de la visite d'expertise (à distinguer du rendez-vous planifié) :
  // sans elle le backend datait la visite du jour du clic, et le SLA du rapport
  // écrit — J+10 ouvrés à partir de la visite — s'en trouvait faux.
  const [expertiseDate, setExpertiseDate] = useState('');
  // Confirmation explicite quand la visite est constatée avant le rendez-vous prévu.
  const [forceExpertise, setForceExpertise] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [insurerReference, setInsurerReference] = useState('');
  const [liabilityValue, setLiabilityValue] = useState('');
  const [manualExpert, setManualExpert] = useState(false);
  const [manualExpertName, setManualExpertName] = useState('');
  const [manualExpertPhone, setManualExpertPhone] = useState('');
  const [manualExpertEmail, setManualExpertEmail] = useState('');
  const [manualGarage, setManualGarage] = useState(false);
  const [manualGarageName, setManualGarageName] = useState('');
  const [manualGaragePhone, setManualGaragePhone] = useState('');
  const [manualGarageEmail, setManualGarageEmail] = useState('');
  const [manualGarageAddress, setManualGarageAddress] = useState('');
  const [expertReportFile, setExpertReportFile] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [vehiculeRestitueAt, setVehiculeRestitueAt] = useState('');
  const [kmRestitution, setKmRestitution] = useState('');
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [, setLoadingOutputs] = useState(false);
  const [errors, setErrors] = useState({});

  const config = WORKFLOW_CONFIGS[workflowType] || WORKFLOW_CONFIGS.claim;
  const availableTransitions = config.transitions[currentStatus] || [];
  const currentStatusInfo = config.statuses[currentStatus];

  // Pre-fill amounts and supplier from prior steps when selecting transitions
  useEffect(() => {
    if (selectedStatus === 'repair_authorized' && contextData?.montant_expertise && !repairAmount) {
      setRepairAmount(String(contextData.montant_expertise));
    }
    // Pre-fill montant_expertise from existing value or initial estimate for rapport_recu
    if ((selectedStatus === 'rapport_recu' || selectedStatus === 'expertise_realisee') && !montantExpertise) {
      const prefill = contextData?.montant_expertise || contextData?.estimated_amount;
      if (prefill) setMontantExpertise(String(prefill));
    }
    // Pre-fill garage at PEC from declaration garage (same garage 90%+ of the time)
    if (selectedStatus === 'prise_en_charge_demandee' && contextData?.garage_supplier_id && !selectedSupplier) {
      setSelectedSupplier(String(contextData.garage_supplier_id));
    }
    // Date de visite : par défaut le rendez-vous s'il est déjà passé, sinon
    // aujourd'hui. Jamais une date future — c'est précisément le bug corrigé.
    if (selectedStatus === 'expertise_realisee' && !expertiseDate) {
      const today = calendarDay(new Date());
      const rdv = calendarDay(contextData?.expert_rdv_date);
      setExpertiseDate(rdv && rdv <= today ? rdv : today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  // Le composant reste monté entre deux ouvertures : sans cette remise à zéro,
  // la date de visite saisie sur un dossier serait reproposée sur le suivant.
  useEffect(() => {
    if (!isOpen) {
      setExpertiseDate('');
      setForceExpertise(false);
    }
  }, [isOpen, entityId]);

  // Auto-select status when only one transition available (skip useless click)
  useEffect(() => {
    if (isOpen && availableTransitions.length === 1 && !selectedStatus) {
      setSelectedStatus(availableTransitions[0]);
    }
  }, [isOpen, availableTransitions, selectedStatus]);

  // Load suppliers when needed — auto-switch to manual if none found
  useEffect(() => {
    if (!selectedStatus) return;

    const supplierCategory = config.supplierCategories?.[selectedStatus];
    if (supplierCategory) {
      loadSuppliers(supplierCategory).then(() => {
        // Will check suppliers after state update via separate effect
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, workflowType]);

  // Auto-switch to manual mode when no suppliers found, pre-fill from claim data
  useEffect(() => {
    if (loadingSuppliers) return;
    const supplierCategory = config.supplierCategories?.[selectedStatus];
    if (supplierCategory === 'garage' && suppliers.length === 0 && !loadingSuppliers) {
      // Only switch to manual mode if we don't already have a garage from a previous step
      if (!contextData?.garage_supplier_id) {
        setManualGarage(true);
        // Pre-fill from existing claim garage data
        if (contextData?.garage_name && !manualGarageName) {
          setManualGarageName(contextData.garage_name);
          if (contextData.garage_phone) setManualGaragePhone(contextData.garage_phone);
          if (contextData.garage_email) setManualGarageEmail(contextData.garage_email);
          if (contextData.garage_address) setManualGarageAddress(contextData.garage_address);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, loadingSuppliers, selectedStatus]);

  // Load expected outputs when status is selected
  useEffect(() => {
    if (!selectedStatus) {
      setOutputs([]);
      return;
    }
    loadOutputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, workflowType, currentStatus]);

  const loadSuppliers = async (category) => {
    setLoadingSuppliers(true);
    try {
      const res = await supplierAPI.getAll({ category, status: 'active' });
      setSuppliers(res.data || []);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const loadOutputs = async () => {
    setLoadingOutputs(true);
    try {
      const res = await workflowOutputAPI.getForTransition(workflowType, currentStatus, selectedStatus);
      setOutputs(res.data || []);
    } catch (err) {
      console.error('Failed to load workflow outputs:', err);
      setOutputs([]);
    } finally {
      setLoadingOutputs(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    const required = config.requiredFields?.[selectedStatus] || [];

    if (!selectedStatus) {
      newErrors.status = t('common.workflow.validation.statusRequired');
    }

    if (required.includes('rejection_reason') && !rejectionReason.trim()) {
      newErrors.rejectionReason = t('common.workflow.validation.rejectionReasonRequired');
    }

    if (required.includes('cancellation_reason') && !rejectionReason.trim()) {
      newErrors.rejectionReason = t('common.workflow.validation.cancellationReasonRequired');
    }

    if (required.includes('failure_reason') && !rejectionReason.trim()) {
      newErrors.rejectionReason = t('common.workflow.validation.failureReasonRequired');
    }

    if (required.includes('insurer_reference') && !insurerReference.trim()) {
      newErrors.insurerReference = t('common.workflow.validation.insurerReferenceRequired');
    }
    if (required.includes('liability') && (!liabilityValue || liabilityValue === 'unknown')) {
      newErrors.liabilityValue = t('common.workflow.validation.liabilityRequired');
    }

    if (required.includes('expert_supplier_id')) {
      // Expert: accept either supplier selection OR manual entry
      if (manualExpert) {
        if (!manualExpertName.trim()) {
          newErrors.manualExpertName = t('common.workflow.validation.expertNameRequired');
        }
      } else if (!selectedSupplier) {
        newErrors.supplier = t('common.workflow.validation.supplierOrManualRequired');
      }
    } else if (required.includes('garage_supplier_id')) {
      // Garage: accept either supplier selection OR manual entry
      if (manualGarage) {
        if (!manualGarageName.trim()) {
          newErrors.manualGarageName = t('common.workflow.validation.garageNameRequired');
        }
      } else if (!selectedSupplier) {
        newErrors.supplier = t('common.workflow.validation.supplierOrManualRequired');
      }
    } else if (required.includes('supplier_id') && !selectedSupplier) {
      newErrors.supplier = t('common.workflow.validation.supplierRequired');
    }

    if (required.includes('repair_amount') && (!repairAmount || parseFloat(repairAmount) <= 0)) {
      newErrors.repairAmount = t('common.workflow.validation.repairAmountRequired');
    }

    if (required.includes('indemnification_recipient') && !indemnificationRecipient) {
      newErrors.indemnificationRecipient = t('common.workflow.validation.indemnificationRecipientRequired');
    }

    if (required.includes('invoice_amount') && (!invoiceAmount || parseFloat(invoiceAmount) <= 0)) {
      newErrors.invoiceAmount = t('common.workflow.validation.invoiceAmountRequired');
    }

    if (required.includes('montant_expertise') && (!montantExpertise || parseFloat(montantExpertise) <= 0)) {
      newErrors.montantExpertise = t('common.workflow.validation.expertAmountRequired');
    }

    if (required.includes('expert_conclusion') && !expertConclusion) {
      newErrors.expertConclusion = t('common.workflow.validation.expertConclusionRequired');
    }

    // Une visite d'expertise ne se constate ni dans le futur, ni avant le
    // rendez-vous pris — sauf confirmation explicite que l'expert est passé plus tôt.
    if (selectedStatus === 'expertise_realisee') {
      const today = calendarDay(new Date());
      const rdv = calendarDay(contextData?.expert_rdv_date);
      if (!expertiseDate) {
        newErrors.expertiseDate = t('common.workflow.validation.expertiseDateRequired');
      } else if (expertiseDate > today) {
        newErrors.expertiseDate = t('common.workflow.validation.expertiseDateFuture');
      } else if (rdv && expertiseDate < rdv && !forceExpertise) {
        newErrors.expertiseDate = t('common.workflow.validation.expertiseBeforeRdv', { date: fmt.date(rdv) });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const transitionData = {
        to_status: selectedStatus,
        action_notes: notes.trim() || null,
        rejection_reason: rejectionReason.trim() || null,
        triggered_by: currentUser?.id,
        triggered_by_name: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : null
      };

      // Add AR fields (insurer reference + liability)
      if (insurerReference.trim()) {
        transitionData.reference_assureur = insurerReference.trim();
      }
      if (liabilityValue && liabilityValue !== 'unknown') {
        transitionData.liability = liabilityValue;
      }

      // Add supplier or manual expert/garage info
      if (manualExpert && selectedStatus === 'expert_pending') {
        transitionData.expert_name = manualExpertName.trim();
        transitionData.expert_phone = manualExpertPhone.trim() || null;
        transitionData.expert_email = manualExpertEmail.trim() || null;
        // Also create as supplier so expert appears in providers list
        try {
          const newSupplier = await supplierAPI.create({
            name: manualExpertName.trim(),
            category: 'expert',
            phone: manualExpertPhone.trim() || null,
            email: manualExpertEmail.trim() || null,
            status: 'active'
          });
          if (newSupplier.ok && newSupplier.data?.id) {
            transitionData.expert_supplier_id = newSupplier.data.id;
          }
        } catch (err) {
          console.error('Failed to create expert supplier:', err);
          // Non-blocking: claim still has expert_name/phone/email even without supplier link
        }
      } else if (manualGarage && needsSupplier === 'garage') {
        // Auto-create supplier then use its ID
        try {
          const newSupplier = await supplierAPI.create({
            name: manualGarageName.trim(),
            category: 'garage',
            phone: manualGaragePhone.trim() || null,
            email: manualGarageEmail.trim() || null,
            address: manualGarageAddress.trim() || null,
            status: 'active'
          });
          if (newSupplier.ok && newSupplier.data?.id) {
            transitionData.garage_supplier_id = newSupplier.data.id;
          }
        } catch (err) {
          console.error('Failed to create garage supplier:', err);
        }
      } else if (selectedSupplier) {
        const supplierKey = config.supplierCategories?.[selectedStatus] === 'expert'
          ? 'expert_supplier_id'
          : config.supplierCategories?.[selectedStatus] === 'garage'
          ? 'garage_supplier_id'
          : 'supplier_id';
        transitionData[supplierKey] = parseInt(selectedSupplier);
      }

      // Add repair amount if applicable
      if (repairAmount) {
        transitionData.repair_amount = parseFloat(repairAmount);
      }

      // Add indemnification recipient if set
      if (indemnificationRecipient) {
        transitionData.indemnification_recipient = indemnificationRecipient;
      }

      // Add garage invoice amount if set
      if (invoiceAmount) {
        transitionData.montant_facture_garage = parseFloat(invoiceAmount);
      }

      // Add vehicle restitution data if set (optional at reparation-terminee)
      if (vehiculeRestitueAt) {
        transitionData.vehicule_restitue_at = vehiculeRestitueAt;
      }
      if (kmRestitution) {
        transitionData.km_restitution = parseInt(kmRestitution);
      }

      // Add expert-specific data
      if (expertRdvDate) {
        transitionData.expert_rdv_date = expertRdvDate;
      }
      if (montantExpertise) {
        transitionData.montant_expertise = parseFloat(montantExpertise);
      }
      if (expertConclusion) {
        transitionData.expert_conclusion = expertConclusion;
      }
      // Date réelle de la visite : le backend en déduit le SLA du rapport écrit.
      if (selectedStatus === 'expertise_realisee' && expertiseDate) {
        transitionData.expertise_date = expertiseDate;
        if (forceExpertise) transitionData.force = true;
      }

      // Upload expert report document if attached (before transition completes)
      if (expertReportFile && entityId) {
        try {
          await documentAPI.upload(expertReportFile, {
            scope: 'CLAIM',
            reference_id: `CLM_${String(entityId).padStart(6, '0')}`,
            document_type: 'EXPERT_REPORT',
            notes: `Rapport d'expertise — ${expertConclusion === 'vei' ? 'VEI' : 'Réparable'}${montantExpertise ? ` — ${ctxMoney(montantExpertise)}` : ''}`,
          });
        } catch (uploadErr) {
          console.warn('[WorkflowTransition] Expert report upload failed:', uploadErr.message);
        }
      }

      // Upload invoice document if attached
      if (invoiceFile && entityId) {
        try {
          await documentAPI.upload(invoiceFile, {
            scope: 'CLAIM',
            reference_id: `CLM_${String(entityId).padStart(6, '0')}`,
            document_type: 'REPAIR_INVOICE',
            notes: `Facture garage${invoiceAmount ? ` — ${ctxMoney(invoiceAmount)} TTC` : ''}`,
          });
        } catch (uploadErr) {
          console.warn('[WorkflowTransition] Invoice upload failed:', uploadErr.message);
        }
      }

      await onConfirm(transitionData);
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || t('common.workflow.submitError') });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClasses = (colorName) => {
    const colors = {
      gray: 'bg-gray-100 text-gray-800 border-gray-300',
      blue: 'bg-blue-100 text-blue-800 border-blue-300',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      purple: 'bg-purple-100 text-purple-800 border-purple-300',
      cyan: 'bg-cyan-100 text-cyan-800 border-cyan-300',
      emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      green: 'bg-green-100 text-green-800 border-green-300',
      red: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[colorName] || colors.gray;
  };

  if (!isOpen) return null;

  const isRejectionLike = selectedStatus === 'rejected' || selectedStatus === 'cancelled' || selectedStatus === 'failed';
  const needsSupplier = config.supplierCategories?.[selectedStatus];
  const needsRepairAmount = config.requiredFields?.[selectedStatus]?.includes('repair_amount');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('common.workflow.title')}</h2>
            <p className="text-sm text-gray-500">
              {config.label} - {entityReference || entityName || `#${entityId}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Current Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">{t('common.workflow.currentStatus')}</p>
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusBadgeClasses(currentStatusInfo?.color)}`}>
              {currentStatusInfo?.label || currentStatus}
            </span>
          </div>

          {/* Target Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.workflow.newStatus')} <span className="text-red-500">*</span>
            </label>
            {availableTransitions.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                {t('common.workflow.noTransitionAvailable')}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {availableTransitions.map(status => {
                  const statusInfo = config.statuses[status];
                  const isSelected = selectedStatus === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setSelectedStatus(status)}
                      className={`p-3 rounded-lg border-2 text-left transition ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {status === 'rejected' || status === 'cancelled' || status === 'failed' ? (
                          <AlertTriangle className={`w-4 h-4 ${isSelected ? 'text-red-500' : 'text-gray-400'}`} />
                        ) : (
                          <CheckCircle className={`w-4 h-4 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                        )}
                        <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                          {statusInfo?.label || status}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status}</p>
            )}
          </div>

          {/* Rejection/Cancellation Reason */}
          {selectedStatus && isRejectionLike && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {selectedStatus === 'rejected' ? t('common.workflow.reasonLabel.rejected') :
                 selectedStatus === 'cancelled' ? t('common.workflow.reasonLabel.cancelled') :
                 t('common.workflow.reasonLabel.failed')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                  errors.rejectionReason ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('common.workflow.reasonPlaceholder')}
              />
              {errors.rejectionReason && (
                <p className="mt-1 text-sm text-red-600">{errors.rejectionReason}</p>
              )}
            </div>
          )}

          {/* AR Fields: Insurer Reference + Liability (en_instruction transition) */}
          {selectedStatus === 'en_instruction' && (
            <div className="space-y-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">{t('common.workflow.insurerData.title')}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.workflow.insurerData.referenceLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={insurerReference}
                  onChange={(e) => setInsurerReference(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                    errors.insurerReference ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('common.workflow.insurerData.referencePlaceholder')}
                />
                {errors.insurerReference && (
                  <p className="mt-1 text-xs text-red-600">{errors.insurerReference}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.workflow.insurerData.liabilityLabel')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={liabilityValue}
                  onChange={(e) => setLiabilityValue(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                    errors.liabilityValue ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('common.actions.selectPlaceholder')}</option>
                  <option value="not_responsible">{t('common.workflow.liability.notResponsible')}</option>
                  <option value="user_25">{t('common.workflow.liability.responsible25')}</option>
                  <option value="shared_50">{t('common.workflow.liability.shared50')}</option>
                  <option value="user_75">{t('common.workflow.liability.responsible75')}</option>
                  <option value="user_100">{t('common.workflow.liability.responsible100')}</option>
                </select>
                {errors.liabilityValue && (
                  <p className="mt-1 text-xs text-red-600">{errors.liabilityValue}</p>
                )}
                <p className="text-xs text-indigo-600 mt-1">
                  {t('common.workflow.insurerData.liabilityHint')}
                </p>
              </div>
            </div>
          )}

          {/* Supplier Selection */}
          {selectedStatus && needsSupplier && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" />
                {needsSupplier === 'expert' ? t('common.workflow.supplier.labelExpert') :
                 needsSupplier === 'garage' && selectedStatus === 'prise_en_charge_demandee' ? t('common.workflow.supplier.labelGaragePec') :
                 needsSupplier === 'garage' ? t('common.workflow.supplier.labelGarage') :
                 t('common.workflow.supplier.labelDefault')} <span className="text-red-500">*</span>
              </label>

              {/* Toggle: supplier list vs manual entry (expert & garage) */}
              {(needsSupplier === 'expert' || needsSupplier === 'garage') && (() => {
                const isManual = needsSupplier === 'expert' ? manualExpert : manualGarage;
                const toggleManual = needsSupplier === 'expert'
                  ? (v) => { setManualExpert(v); setSelectedSupplier(''); }
                  : (v) => { setManualGarage(v); setSelectedSupplier(''); };
                const activeClass = needsSupplier === 'expert'
                  ? 'bg-purple-100 text-purple-800 border-purple-300'
                  : 'bg-cyan-100 text-cyan-800 border-cyan-300';
                return (
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => toggleManual(false)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                        !isManual ? activeClass : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {t('common.workflow.supplier.fromList')}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleManual(true)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                        isManual ? activeClass : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {t('common.workflow.supplier.newSupplier')}
                    </button>
                  </div>
                );
              })()}

              {/* Manual garage entry */}
              {manualGarage && needsSupplier === 'garage' ? (
                <div className="space-y-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('common.workflow.supplier.garageNameLabel')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={manualGarageName}
                      onChange={(e) => setManualGarageName(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm ${
                        errors.manualGarageName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('common.workflow.supplier.garageNamePlaceholder')}
                    />
                    {errors.manualGarageName && (
                      <p className="mt-1 text-xs text-red-600">{errors.manualGarageName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.workflow.supplier.addressLabel')}</label>
                    <input
                      type="text"
                      value={manualGarageAddress}
                      onChange={(e) => setManualGarageAddress(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                      placeholder={t('common.workflow.supplier.addressPlaceholder')}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.workflow.supplier.phoneLabel')}</label>
                      <input
                        type="tel"
                        value={manualGaragePhone}
                        onChange={(e) => setManualGaragePhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                        placeholder={t('common.workflow.supplier.garagePhonePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.workflow.supplier.emailLabel')}</label>
                      <input
                        type="email"
                        value={manualGarageEmail}
                        onChange={(e) => setManualGarageEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                        placeholder={t('common.workflow.supplier.garageEmailPlaceholder')}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-cyan-700">
                    {t('common.workflow.supplier.garageAutoAddHint')}
                  </p>
                </div>
              ) : manualExpert && needsSupplier === 'expert' ? (
                <div className="space-y-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('common.workflow.supplier.expertNameLabel')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={manualExpertName}
                      onChange={(e) => setManualExpertName(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm ${
                        errors.manualExpertName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('common.workflow.supplier.expertNamePlaceholder')}
                    />
                    {errors.manualExpertName && (
                      <p className="mt-1 text-xs text-red-600">{errors.manualExpertName}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.workflow.supplier.phoneLabel')}</label>
                      <input
                        type="tel"
                        value={manualExpertPhone}
                        onChange={(e) => setManualExpertPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder={t('common.workflow.supplier.expertPhonePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.workflow.supplier.emailLabel')}</label>
                      <input
                        type="email"
                        value={manualExpertEmail}
                        onChange={(e) => setManualExpertEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder={t('common.workflow.supplier.expertEmailPlaceholder')}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-purple-600">
                    {t('common.workflow.supplier.expertAutoAddHint')}
                  </p>
                </div>
              ) : (
                <>
                  {loadingSuppliers ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('common.states.loading')}</span>
                    </div>
                  ) : (
                    <select
                      value={selectedSupplier}
                      onChange={(e) => setSelectedSupplier(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.supplier ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">{t('common.actions.select')}</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name} - {supplier.city || t('common.states.notAvailable')}
                          {supplier.is_preferred && ' ⭐'}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              )}
              {errors.supplier && (
                <p className="mt-1 text-sm text-red-600">{errors.supplier}</p>
              )}
              {selectedStatus === 'prise_en_charge_demandee' && contextData?.garage_supplier_id && selectedSupplier === String(contextData.garage_supplier_id) && (
                <p className="text-xs text-cyan-600 mt-1">
                  {t('common.workflow.supplier.prefilledFromDeclaration', {
                    garage: contextData.garage_name ? ` (${contextData.garage_name})` : ''
                  })}
                </p>
              )}
            </div>
          )}

          {/* Repair Amount */}
          {selectedStatus && needsRepairAmount && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.workflow.repairAmountLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={repairAmount}
                  onChange={(e) => setRepairAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.repairAmount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{ctxSymbol}</span>
              </div>
              {contextData?.montant_expertise && selectedStatus === 'repair_authorized' && (
                <p className="text-xs text-indigo-600 mt-1">
                  {t('common.workflow.repairAmountPrefilled', { amount: ctxMoney(contextData.montant_expertise) })}
                </p>
              )}
              {errors.repairAmount && (
                <p className="mt-1 text-sm text-red-600">{errors.repairAmount}</p>
              )}
            </div>
          )}

          {/* Indemnification Recipient (when authorizing repair) */}
          {selectedStatus === 'repair_authorized' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.workflow.indemnification.title')} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setIndemnificationRecipient('garage')}
                  className={`p-3 rounded-lg border-2 text-left transition ${
                    indemnificationRecipient === 'garage'
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-4 h-4 ${indemnificationRecipient === 'garage' ? 'text-cyan-500' : 'text-gray-400'}`} />
                    <span className={`font-medium ${indemnificationRecipient === 'garage' ? 'text-cyan-700' : 'text-gray-700'}`}>
                      {t('common.workflow.indemnification.garageOption')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('common.workflow.indemnification.garageHint')}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setIndemnificationRecipient('user')}
                  className={`p-3 rounded-lg border-2 text-left transition ${
                    indemnificationRecipient === 'user'
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className={`w-4 h-4 ${indemnificationRecipient === 'user' ? 'text-amber-500' : 'text-gray-400'}`} />
                    <span className={`font-medium ${indemnificationRecipient === 'user' ? 'text-amber-700' : 'text-gray-700'}`}>
                      {t('common.workflow.indemnification.userOption')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('common.workflow.indemnification.userHint')}
                  </p>
                </button>
              </div>
              {errors.indemnificationRecipient && (
                <p className="mt-1 text-sm text-red-600">{errors.indemnificationRecipient}</p>
              )}
            </div>
          )}

          {/* Garage Invoice Amount (at repair completion or when providing invoice) */}
          {(selectedStatus === 'attente_facture_garage' || selectedStatus === 'indemnified' || selectedStatus === 'attente_indemnisation') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.workflow.invoice.amountLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.invoiceAmount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{ctxSymbol}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('common.workflow.invoice.amountHint')}
              </p>
              {errors.invoiceAmount && (
                <p className="mt-1 text-sm text-red-600">{errors.invoiceAmount}</p>
              )}

              {/* Invoice document upload */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Paperclip className="w-4 h-4 inline mr-1" />
                  {t('common.workflow.invoice.fileLabel')}
                </label>
                <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  invoiceFile ? 'border-amber-300 bg-amber-50' : 'border-gray-300 hover:border-amber-300 hover:bg-gray-50'
                }`}>
                  {invoiceFile ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-amber-700">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium truncate max-w-[200px]">{invoiceFile.name}</span>
                        <span className="text-amber-400">{t('common.workflow.fileSizeKo', { size: (invoiceFile.size / 1024).toFixed(0) })}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setInvoiceFile(null)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                      <span className="text-sm text-gray-500">{t('common.workflow.invoice.uploadPrompt')}</span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) setInvoiceFile(e.target.files[0]);
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Vehicle restitution — optional at repair completion */}
          {(selectedStatus === 'attente_facture_garage' || (selectedStatus === 'attente_indemnisation' && currentStatus === 'en_reparation')) && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-3">
              <p className="text-sm font-medium text-teal-700 flex items-center gap-1">
                <Car className="w-4 h-4" />
                {t('common.workflow.restitution.title')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.workflow.restitution.dateLabel')}</label>
                  <input
                    type="date"
                    value={vehiculeRestitueAt}
                    onChange={(e) => setVehiculeRestitueAt(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.workflow.restitution.kmLabel')}</label>
                  <input
                    type="number"
                    value={kmRestitution}
                    onChange={(e) => setKmRestitution(e.target.value)}
                    min="0"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder={t('common.workflow.restitution.kmPlaceholder')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Expert RDV Date (when assigning expert) */}
          {selectedStatus === 'expert_pending' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('common.workflow.expertRdv.dateLabel')}
              </label>
              <input
                type="date"
                value={expertRdvDate}
                onChange={(e) => setExpertRdvDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('common.workflow.expertRdv.hint')}
              </p>
            </div>
          )}

          {/* Date réelle de la visite d'expertise + garde-fou sur le rendez-vous.
              Sans ce bloc, valider « expertise réalisée » une semaine avant le RDV
              passait sans un mot — et faussait le SLA du rapport écrit. */}
          {selectedStatus === 'expertise_realisee' && (() => {
            const today = calendarDay(new Date());
            const rdv = calendarDay(contextData?.expert_rdv_date);
            const rdvIsAhead = rdv && rdv > today;
            const beforeRdv = rdv && expertiseDate && expertiseDate < rdv;
            return (
              <div className="space-y-3">
                {rdv && (
                  <div className={`rounded-lg border px-3 py-2 text-sm ${
                    rdvIsAhead ? 'bg-amber-50 border-amber-300 text-amber-900'
                      : 'bg-purple-50 border-purple-200 text-purple-900'
                  }`}>
                    <div className="flex items-start gap-2">
                      {rdvIsAhead
                        ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        : <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                      <span>
                        {t('common.workflow.expertiseRdvPlanned', { date: fmt.date(rdv) })}
                        {rdvIsAhead && ' — ' + t('common.workflow.expertiseRdvAhead')}
                      </span>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.workflow.expertiseDateLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={expertiseDate}
                    max={today}
                    onChange={(e) => { setExpertiseDate(e.target.value); setForceExpertise(false); }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.expertiseDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('common.workflow.expertiseDateHint')}</p>
                  {errors.expertiseDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.expertiseDate}</p>
                  )}
                </div>
                {beforeRdv && (
                  <label className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={forceExpertise}
                      onChange={(e) => setForceExpertise(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>{t('common.workflow.expertiseBeforeRdvConfirm', { date: fmt.date(rdv) })}</span>
                  </label>
                )}
              </div>
            );
          })()}

          {/* Expert Report Fields (when expertise done or receiving report) */}
          {(selectedStatus === 'expert_done' || selectedStatus === 'expertise_realisee') && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.workflow.expertConclusion.title')} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setExpertConclusion('repairable')}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      expertConclusion === 'repairable'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`w-4 h-4 ${expertConclusion === 'repairable' ? 'text-green-500' : 'text-gray-400'}`} />
                      <span className={`font-medium ${expertConclusion === 'repairable' ? 'text-green-700' : 'text-gray-700'}`}>
                        {t('common.workflow.expertConclusion.repairable')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t('common.workflow.expertConclusion.repairableHint')}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpertConclusion('vei')}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      expertConclusion === 'vei'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`w-4 h-4 ${expertConclusion === 'vei' ? 'text-red-500' : 'text-gray-400'}`} />
                      <span className={`font-medium ${expertConclusion === 'vei' ? 'text-red-700' : 'text-gray-700'}`}>
                        {t('common.workflow.expertConclusion.vei')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t('common.workflow.expertConclusion.veiHint')}</p>
                  </button>
                </div>
                {errors.expertConclusion && (
                  <p className="mt-1 text-sm text-red-600">{errors.expertConclusion}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('claimWorkflow.form.expertAmount', { symbol: ctxSymbol })} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={montantExpertise}
                    onChange={(e) => setMontantExpertise(e.target.value)}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.montantExpertise ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{ctxSymbol}</span>
                </div>
                {errors.montantExpertise && (
                  <p className="mt-1 text-sm text-red-600">{errors.montantExpertise}</p>
                )}
              </div>
            </>
          )}

          {/* Montant expertise (when receiving expert report) */}
          {selectedStatus === 'rapport_recu' && (
            <div>
              {/* Show initial verbal estimate for reference if it exists */}
              {contextData?.montant_expertise_initial && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 mb-3 text-sm">
                  <span className="text-purple-700 font-medium">{t('common.workflow.expertReport.verbalEstimateLabel')} </span>
                  <span className="text-purple-900 font-bold">
                    {ctxMoney(contextData.montant_expertise_initial)}
                  </span>
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('claimWorkflow.form.expertAmountReport', { symbol: ctxSymbol })} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={montantExpertise}
                  onChange={(e) => setMontantExpertise(e.target.value)}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.montantExpertise ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{ctxSymbol}</span>
              </div>
              {errors.montantExpertise && (
                <p className="mt-1 text-sm text-red-600">{errors.montantExpertise}</p>
              )}

              {/* Expert report document upload */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Paperclip className="w-4 h-4 inline mr-1" />
                  {t('common.workflow.expertReport.fileLabel')}
                </label>
                <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  expertReportFile ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300 hover:border-indigo-300 hover:bg-gray-50'
                }`}>
                  {expertReportFile ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-indigo-700">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium truncate max-w-[200px]">{expertReportFile.name}</span>
                        <span className="text-indigo-400">{t('common.workflow.fileSizeKo', { size: (expertReportFile.size / 1024).toFixed(0) })}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpertReportFile(null)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                      <span className="text-sm text-gray-500">{t('common.workflow.expertReport.uploadPrompt')}</span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) setExpertReportFile(e.target.files[0]);
                        }}
                      />
                    </label>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {t('common.workflow.expertReport.aiHint')}
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-1" />
              {t('common.workflow.notesLabel')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('common.workflow.notesPlaceholder')}
            />
          </div>

          {/* Expected Outputs */}
          {selectedStatus && outputs.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800 mb-2">
                {t('common.workflow.autoActionsTitle')}
              </p>
              <div className="space-y-2">
                {outputs.map((output, idx) => {
                  const Icon = OUTPUT_ICONS[output.output_type] || Bell;
                  return (
                    <div key={idx} className="flex items-center gap-2 text-sm text-blue-700">
                      <Icon className="w-4 h-4" />
                      <span>
                        {output.output_type === 'email' && t('common.workflow.outputs.email')}
                        {output.output_type === 'document' && t('common.workflow.outputs.document')}
                        {output.output_type === 'notification' && t('common.workflow.outputs.notification')}
                        {output.output_type === 'task' && t('common.workflow.outputs.task')}
                        {output.output_type === 'event' && t('common.workflow.outputs.event')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Error message */}
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <User className="w-4 h-4" />
            <span>{t('common.workflow.byUser', { name: currentUser?.firstName || t('common.workflow.defaultUserName') })}</span>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition"
              disabled={loading}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || availableTransitions.length === 0 || !selectedStatus}
              className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                isRejectionLike
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common.workflow.submitting')}
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4" />
                  {t('common.actions.confirm')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkflowTransitionModal;
